"use client";

import {
  Box,
  Center,
  CloseButton,
  Drawer,
  Flex,
  HStack,
  Icon,
  Image,
  Portal,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { PaymentLoading } from "../../../app/[locale]/pricing/components/PaymentLoading";
import { MdOutlineShoppingCartCheckout } from "react-icons/md";

export interface CheckoutProductSummary {
  imageUrl?: string;
  name: string;
  subtotal: number;
  tax: number;
  discount: number;
  discountCode?: string;
  total: number;
  currencyCode: string;
  price_cycle?: string;
}

function ProductSummarySkeleton() {
  return (
    <VStack align="stretch" gap={3}>
      <Flex gap={3} flexDir="column">
        <Skeleton height="10rem" rounded="md" />
        <Skeleton height="28px" width="70%" />
      </Flex>
      <Flex justify="space-between" align="center">
        <Skeleton height="20px" width="72px" />
        <Skeleton height="20px" width="88px" />
      </Flex>
      <Flex justify="space-between" align="center">
        <Skeleton height="20px" width="42px" />
        <Skeleton height="20px" width="120px" />
      </Flex>
      <Box borderTopWidth="1px" pt={2}>
        <Flex justify="space-between" align="center">
          <Skeleton height="18px" width="40px" />
          <Skeleton height="18px" width="92px" />
        </Flex>
      </Box>
    </VStack>
  );
}

interface PaymentDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutProduct: CheckoutProductSummary | null;
  checkoutCurrency: Intl.NumberFormat | null;
  frameTarget: string;
  isPaymentLoading: boolean;
  title?: string;
}

export default function PaymentDrawer({
  isOpen,
  onOpenChange,
  checkoutProduct,
  checkoutCurrency,
  frameTarget,
  isPaymentLoading,
  title,
}: PaymentDrawerProps) {
  const t = useTranslations("pricing.checkout");

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(event) => onOpenChange(event.open)}
      placement={{ base: "bottom", md: "end" }}
      size={{
        base: "full",
        md: "lg",
        lg: checkoutProduct?.total ? "xl" : "lg",
      }}
      unmountOnExit
      lazyMount
    >
      <Portal>
        <Drawer.Backdrop backdropFilter="blur(2px)" />
        <Drawer.Positioner padding="4">
          <Drawer.Content rounded="3xl" h="calc(100dvh - 2rem)">
            <Drawer.Header bg="primary.600" roundedTop="3xl" py={4}>
              <Drawer.Title textStyle="lg" fontWeight="semibold" color="white">
                <HStack gap={2} align="center">
                  <Icon
                    as={MdOutlineShoppingCartCheckout}
                    boxSize={6}
                    color="inherit"
                  />
                  <Text textStyle="xl" fontWeight="semibold" color="inherit">
                    {title || t("title")}
                  </Text>
                </HStack>
              </Drawer.Title>
              <Drawer.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Drawer.CloseTrigger>
            </Drawer.Header>
            <Drawer.Body mt={4}>
              <Flex
                gap={8}
                direction={{ base: "column", md: "row" }}
                align={{ base: "stretch", md: "flex-start" }}
                justify="center"
              >
                {checkoutProduct && checkoutProduct.subtotal > 0 ? (
                  <Box
                    p={4}
                    rounded="3xl"
                    borderWidth="0.5px"
                    borderColor="gray.200"
                    bg="gray.50"
                    shadow="shadow-md"
                  >
                    {/* <Text fontWeight="semibold" mb={4}>
                      {t("productSummary")}
                    </Text> */}
                    {checkoutProduct.total ? (
                      <VStack align="stretch" gap={3}>
                        <Flex gap={3} flexDir="column" mb={4}>
                          {checkoutProduct.imageUrl ? (
                            <Image
                              src={checkoutProduct.imageUrl}
                              alt={checkoutProduct.name}
                              boxSize="16rem"
                              h="10rem"
                              rounded="md"
                              objectFit="contain"
                            />
                          ) : null}
                          <Text
                            textStyle="2xl"
                            fontWeight="medium"
                            color="dark.800"
                          >
                            {checkoutProduct.name}{" "}
                            <Text
                              as="span"
                              textStyle="lg"
                              color="neutral.600"
                              fontStyle="italic"
                            >
                              *{checkoutProduct.price_cycle}
                            </Text>
                          </Text>
                        </Flex>
                        <Box id="product-summary-container">
                          <Flex justify="space-between">
                            <Text color="neutral.600">{t("subtotal")}</Text>
                            <Text fontWeight="regular">
                              {checkoutCurrency?.format(
                                checkoutProduct.subtotal,
                              )}
                            </Text>
                          </Flex>
                          <Flex justify="space-between" my={2}>
                            <Text color="neutral.600">{t("tax")}</Text>
                            <Text fontWeight="regular">
                              {checkoutCurrency?.format(checkoutProduct.tax)}
                            </Text>
                          </Flex>
                          {checkoutProduct.discount > 0 && (
                            <Flex justify="space-between">
                              <Text color="neutral.600" textStyle="xs">
                                {checkoutProduct.discountCode
                                  ? t("discountWithCode", {
                                      code: checkoutProduct.discountCode,
                                    })
                                  : t("discount")}
                              </Text>
                              <Text
                                textStyle="sm"
                                fontWeight="regular"
                                color="green.600"
                              >
                                -
                                {checkoutCurrency?.format(
                                  checkoutProduct.discount,
                                )}
                              </Text>
                            </Flex>
                          )}
                          <Flex
                            justify="space-between"
                            align="center"
                            pt={2}
                            borderTopWidth="1px"
                          >
                            <Text textStyle="sm">{t("total")}</Text>
                            <Text
                              textStyle="2xl"
                              fontWeight="light"
                              color="primary.600"
                            >
                              {checkoutCurrency?.format(checkoutProduct.total)}
                            </Text>
                          </Flex>
                        </Box>
                      </VStack>
                    ) : (
                      <ProductSummarySkeleton />
                    )}
                  </Box>
                ) : null}
                <Center minW={{ base: "full", md: "550px" }}>
                  <PaymentLoading isLoading={isPaymentLoading} />
                  <Box
                    className={frameTarget}
                    id="payment-drawer-iframe"
                    minH="460px"
                    w="full"
                    display={isPaymentLoading ? "none" : "block"}
                  />
                </Center>
              </Flex>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
